import React, { useEffect, useMemo, useState } from 'react';
import { Dialog } from 'primereact/dialog';
import { Button } from 'primereact/button';
import { InputText } from 'primereact/inputtext';
import { Dropdown } from 'primereact/dropdown';
import {
  CrmAccountEntity,
  CrmAccountType,
  CrmVatStatus,
} from '../../../../api/types/crm/accounts/crm-account-entity';
import { updateCrmAccountOperation } from '../operations/update-crm-account-operation';

interface EditCrmAccountDialogProps {
  visible: boolean;
  account: CrmAccountEntity | null;
  onHide: () => void;
  onSuccess: () => void;
}

const normalizeOptionalString = (value: string) => {
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : null;
};

export const EditCrmAccountDialog: React.FC<EditCrmAccountDialogProps> = ({
  visible,
  account,
  onHide,
  onSuccess,
}) => {
  const [accountName, setAccountName] = useState<string>('');
  const [accountType, setAccountType] = useState<CrmAccountType | null>(null);
  const [vatStatus, setVatStatus] = useState<CrmVatStatus | null>(null);
  const [registrationNumber, setRegistrationNumber] = useState<string>('');
  const [email, setEmail] = useState<string>('');
  const [phone, setPhone] = useState<string>('');
  const [address, setAddress] = useState<string>('');
  const [notes, setNotes] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);

  useEffect(() => {
    if (!account || !visible) return;
    setAccountName(account.accountName ?? '');
    setAccountType(account.accountType ?? null);
    setVatStatus(account.vatStatus ?? null);
    setRegistrationNumber(account.registrationNumber ?? '');
    setEmail(account.email ?? '');
    setPhone(account.phone ?? '');
    setAddress(account.address ?? '');
    setNotes(account.notes ?? '');
  }, [account, visible]);

  const accountTypeOptions = useMemo(
    () => [
      { label: '— ללא —', value: null },
      { label: 'פרטי', value: CrmAccountType.Individual },
      { label: 'חברה בע״מ', value: CrmAccountType.Company },
      { label: 'עמותה', value: CrmAccountType.Association },
      { label: 'מלכ״ר / חל״צ', value: CrmAccountType.NonProfit },
      { label: 'עצמאי', value: CrmAccountType.SelfEmployed },
      { label: 'גוף ממשלתי', value: CrmAccountType.Government },
      { label: 'מוסד', value: CrmAccountType.Institution },
      { label: 'אחר', value: CrmAccountType.Other },
    ],
    [],
  );

  const vatStatusOptions = useMemo(
    () => [
      { label: '— ללא —', value: null },
      { label: 'עוסק מורשה', value: CrmVatStatus.VatRegistered },
      { label: 'עוסק פטור', value: CrmVatStatus.VatExempt },
    ],
    [],
  );

  const handleClose = () => {
    if (loading) return;
    onHide();
  };

  const isValid = accountName.trim().length > 0 && !!account;

  const handleApprove = async () => {
    if (!account || !isValid || loading) return;

    await updateCrmAccountOperation(
      account.id,
      {
        accountName: accountName.trim(),
        accountType,
        vatStatus,
        registrationNumber: normalizeOptionalString(registrationNumber),
        email: normalizeOptionalString(email),
        phone: normalizeOptionalString(phone),
        address: normalizeOptionalString(address),
        notes: normalizeOptionalString(notes),
      },
      setLoading,
      () => {
        onHide();
        onSuccess();
      },
    );
  };

  return (
    <Dialog
      header={`עדכון ארגון${account ? ` - ${account.accountName}` : ''}`}
      visible={visible}
      onHide={handleClose}
      style={{ width: '520px', direction: 'rtl' }}
      modal
      closable={!loading}
      footer={
        <div
          style={{
            display: 'flex',
            justifyContent: 'flex-end',
            gap: '0.5rem',
          }}
        >
          <Button
            label="ביטול"
            icon="pi pi-times"
            severity="secondary"
            text
            onClick={handleClose}
            disabled={loading}
          />
          <Button
            label={loading ? 'שומר...' : 'אישור'}
            icon={loading ? 'pi pi-spin pi-spinner' : 'pi pi-check'}
            onClick={handleApprove}
            disabled={!isValid || loading}
            loading={loading}
          />
        </div>
      }
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
        <div className="p-field">
          <label
            style={{
              display: 'block',
              marginBottom: '0.5rem',
              fontWeight: 600,
            }}
          >
            שם הארגון *
          </label>
          <InputText
            value={accountName}
            onChange={(e) => setAccountName(e.target.value)}
            style={{ width: '100%' }}
            disabled={loading}
          />
        </div>

        <div
          style={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            gap: '1rem',
          }}
        >
          <div className="p-field">
            <label style={{ display: 'block', marginBottom: '0.5rem' }}>
              סוג ארגון
            </label>
            <Dropdown
              value={accountType}
              options={accountTypeOptions}
              onChange={(e) => setAccountType(e.value)}
              optionLabel="label"
              optionValue="value"
              placeholder="בחר סוג"
              style={{ width: '100%' }}
              disabled={loading}
            />
          </div>

          <div className="p-field">
            <label style={{ display: 'block', marginBottom: '0.5rem' }}>
              סטטוס מע״מ
            </label>
            <Dropdown
              value={vatStatus}
              options={vatStatusOptions}
              onChange={(e) => setVatStatus(e.value)}
              optionLabel="label"
              optionValue="value"
              placeholder="בחר סטטוס"
              style={{ width: '100%' }}
              disabled={loading}
            />
          </div>
        </div>

        <div className="p-field">
          <label style={{ display: 'block', marginBottom: '0.5rem' }}>
            מס׳ רישום
          </label>
          <InputText
            value={registrationNumber}
            onChange={(e) => setRegistrationNumber(e.target.value)}
            style={{ width: '100%' }}
            disabled={loading}
          />
        </div>

        <div
          style={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            gap: '1rem',
          }}
        >
          <div className="p-field">
            <label style={{ display: 'block', marginBottom: '0.5rem' }}>
              אימייל
            </label>
            <InputText
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              style={{ width: '100%' }}
              disabled={loading}
            />
          </div>

          <div className="p-field">
            <label style={{ display: 'block', marginBottom: '0.5rem' }}>
              טלפון
            </label>
            <InputText
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              style={{ width: '100%' }}
              disabled={loading}
            />
          </div>
        </div>

        <div className="p-field">
          <label style={{ display: 'block', marginBottom: '0.5rem' }}>
            כתובת
          </label>
          <InputText
            value={address}
            onChange={(e) => setAddress(e.target.value)}
            style={{ width: '100%' }}
            disabled={loading}
          />
        </div>

        <div className="p-field">
          <label style={{ display: 'block', marginBottom: '0.5rem' }}>
            הערות
          </label>
          <InputText
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            style={{ width: '100%' }}
            disabled={loading}
          />
        </div>
      </div>
    </Dialog>
  );
};
